import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { useAuth } from '../../components/AuthContext';
import {
  Users, Search, UserCircle, Shield, ChevronRight, Ban, CheckCircle
} from 'lucide-react';
import Pagination from '../../components/Pagination';

/**
 * UserManagement — Admin page to view and manage all user accounts.
 */
const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [toggling, setToggling] = useState(null);
  const ITEMS_PER_PAGE = 10;

  const toggleUserStatus = async (userId, currentlyActive) => {
    setToggling(userId);
    try {
      await api.patch(`/admin/users/${userId}`, { isActive: !currentlyActive });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, isActive: !currentlyActive } : u));
      toast.success(currentlyActive ? 'User suspended' : 'User activated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update user');
    } finally {
      setToggling(null);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page,
          limit: ITEMS_PER_PAGE,
        });
        if (roleFilter !== 'ALL') params.set('role', roleFilter);
        if (searchTerm.trim()) params.set('search', searchTerm.trim());
        const res = await api.get(`/admin/users?${params}`);
        const data = res?.data?.data || [];
        setUsers(Array.isArray(data) ? data : []);
        setTotalPages(res?.data?.pagination?.totalPages || 1);
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };
    const debounce = setTimeout(fetchUsers, searchTerm ? 300 : 0);
    return () => clearTimeout(debounce);
  }, [page, roleFilter, searchTerm]);

  const roleColors = {
    USER: 'bg-blue-100 text-blue-700',
    DOCTOR: 'bg-teal-100 text-teal-700',
    ADMIN: 'bg-purple-100 text-purple-700',
  };

  return (
    <div className="space-y-6 py-6">
      <div>
        <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">User Management</h2>
        <p className="text-slate-500 text-sm mt-1">View and manage all platform users</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div className="flex gap-2">
          {['ALL', 'USER', 'DOCTOR', 'ADMIN'].map((role) => (
            <button
              key={role}
              onClick={() => { setRoleFilter(role); setPage(1); }}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                roleFilter === role
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {role === 'ALL' ? 'All' : role === 'USER' ? 'Patients' : role === 'DOCTOR' ? 'Doctors' : 'Admins'}
            </button>
          ))}
        </div>
      </div>

      {/* User count */}
      <p className="text-sm text-slate-500 font-medium">{users.length} users found</p>


      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-16 rounded-2xl animate-pulse bg-slate-100" />)}
        </div>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-outline-variant/10">
          <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <h3 className="font-bold text-lg text-slate-900 mb-1">No users found</h3>
          <p className="text-sm text-slate-500">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u._id} className="bg-white rounded-xl p-4 border border-outline-variant/10 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center text-white font-extrabold text-sm shrink-0">
                {(u.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm text-slate-900 break-words">{u.name}</h4>
                <p className="text-xs text-slate-500 break-all">{u.email}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${roleColors[u.role] || 'bg-slate-100 text-slate-600'}`}>
                {u.role}
              </span>
              {u.isActive === false && (
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-600">Suspended</span>
              )}
              {u._id !== currentUser?.id && (
                <button
                  onClick={() => toggleUserStatus(u._id, u.isActive !== false)}
                  disabled={toggling === u._id}
                  className={`p-2 rounded-lg transition-all ${u.isActive === false
                    ? 'text-emerald-600 hover:bg-emerald-50'
                    : 'text-red-500 hover:bg-red-50'
                  } ${toggling === u._id ? 'opacity-50' : ''}`}
                  title={u.isActive === false ? 'Activate user' : 'Suspend user'}
                >
                  {u.isActive === false
                    ? <CheckCircle className="w-4 h-4" />
                    : <Ban className="w-4 h-4" />
                  }
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onPageChange={(p) => setPage(p)} />
    </div>
  );
};

export default UserManagement;
